// tRPC client for the SPA → BFF tier.
//
// **Spec § Streaming (round-3 disambiguation):** tRPC is reserved for
// queries + mutations only. The SPA does NOT use
// `httpSubscriptionLink`. SSE streams are handled by `EventSource`
// via `streams/session-stream.ts`. Two transports, two purposes.
//
// **What this module exposes:**
//   - `trpc` — the React-Query-flavored tRPC client (`createTRPCReact`).
//     Surfaces `useQuery`, `useMutation`, `useUtils`, etc.
//   - `vanillaTrpcClient` — the underlying vanilla client (for code
//     paths that need to call procedures outside React, e.g. router
//     loaders or one-off boot probes).
//   - `createTrpcReactClient` — the per-app factory used by
//     `main.tsx`. Returns the client + its corresponding QueryClient.
//
// **Why `httpLink` ONLY:**
//   - `httpLink` POSTs JSON for both queries + mutations. It does NOT
//     hold a long-lived connection. Cookies ride on the request via
//     `credentials: 'include'`.
//   - We deliberately DO NOT add `httpSubscriptionLink` or
//     `wsLink` — those are for live subscriptions, which the spec
//     routes through plain `EventSource` instead. Adding the link
//     here would tempt future contributors to build a tRPC
//     subscription path that contradicts the spec; we keep the
//     temptation surface zero.
//
// **`credentials: 'include'`** is required so the browser sends the
// `sbp_session` cookie cross-origin. The cookie plugin on the BFF
// reads it and stamps `req.identity`. tRPC's `fetch` option exposes
// this — we use `fetch` directly (no third-party wrapper).

import type { BffRouter } from '@sb-ai-platform/bff';
import { QueryClient } from '@tanstack/react-query';
// `httpLink` is owned by `@trpc/client` — `@trpc/react-query` re-exports
// it as a convenience, but the canonical import path is the client
// package. Importing from the canonical source (a) matches the tRPC v11
// docs, (b) avoids a transitive coupling where dropping `@trpc/react-
// query` in a hypothetical future router-loader path would break this
// module, and (c) keeps the symbol where its source-import test
// (`trpc-client.test.ts`) can verify the no-`httpSubscriptionLink`
// contract against a stable import surface.
import { type CreateTRPCClient, createTRPCClient, httpLink } from '@trpc/client';
import { type CreateTRPCReact, createTRPCReact } from '@trpc/react-query';

import { getActiveClientOtelHandle } from './instrumentation/client-otel.js';

/**
 * The BFF console-session retryable-refresh status (fn-45.4). The BFF
 * returns this NON-revoking status when a benign multi-instance refresh
 * race loses; the winning request installs the rotated `__Host-` cookies,
 * so a one-shot retry succeeds. Mirrors
 * `CONSOLE_REFRESH_RETRYABLE_STATUS` in `@sb-ai-platform/bff`.
 */
export const SESSION_REFRESH_RETRY_STATUS = 503 as const;

/**
 * Marker header the BFF sets ONLY on the session-refresh retryable 503
 * (`CONSOLE_REFRESH_RETRYABLE_HEADER` in `@sb-ai-platform/bff`). We retry only
 * when BOTH the 503 status AND this header are present — so an unrelated
 * upstream 503 (or a mutation/upload that 503s) is surfaced to the caller, not
 * silently resent as a second POST.
 */
export const SESSION_REFRESH_RETRY_HEADER = 'x-sb-session-refresh-retry' as const;

/** Cap the Retry-After honor so a hostile/large value can't stall the SPA. */
const MAX_RETRY_AFTER_MS = 3000;

function retryAfterMs(res: Response): number {
  const header = res.headers.get('retry-after');
  if (header === null) return 250;
  const seconds = Number(header);
  if (!Number.isFinite(seconds) || seconds < 0) return 250;
  return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
}

/**
 * Wrap a fetch with a ONE-SHOT retry on the BFF `503 SESSION_REFRESH_RETRY`
 * status. Exported for tests. The retry waits `Retry-After` (capped) so the
 * winning concurrent refresh has installed the rotated cookies. We retry ONLY
 * when the response is a 503 carrying the BFF's explicit session-refresh marker
 * header — every other status (incl. terminal 401) AND any unmarked 503 (an
 * unrelated upstream 503, or a mutation/upload that 503s) passes through
 * unchanged, so a non-idempotent request is never silently resent. We retry at
 * most once: a persistent marked-503 surfaces to the caller, never a loop.
 */
export function retryableRefreshFetch(inner: typeof fetch): typeof fetch {
  return (async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const first = await inner(input, init);
    if (
      first.status !== SESSION_REFRESH_RETRY_STATUS ||
      first.headers.get(SESSION_REFRESH_RETRY_HEADER) !== '1'
    )
      return first;
    // The caller may have aborted while the first response was in flight —
    // don't even start the delay; surface the abort.
    const signal = init?.signal ?? null;
    if (signal?.aborted) {
      throw signal.reason instanceof Error
        ? signal.reason
        : new DOMException('The operation was aborted.', 'AbortError');
    }
    // Drain the first body so the connection is reusable.
    await first.text().catch(() => undefined);
    // Honor `init.signal` during the Retry-After delay: race the sleep
    // against the abort signal so an aborted request doesn't wait out the
    // full (capped) delay before bailing. On abort we reject with the
    // abort reason and skip the retry fetch entirely.
    const aborted = await new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve(false);
      }, retryAfterMs(first));
      const onAbort = () => {
        clearTimeout(timer);
        resolve(true);
      };
      signal?.addEventListener('abort', onAbort, { once: true });
    });
    if (aborted) {
      throw signal?.reason instanceof Error
        ? signal.reason
        : new DOMException('The operation was aborted.', 'AbortError');
    }
    return inner(input, init);
  }) as typeof fetch;
}

/**
 * The React-flavored tRPC client. Components consume it via
 * `trpc.useQuery(...)` / `trpc.useMutation(...)`.
 *
 * **Why we export a module-level singleton rather than a hook factory:**
 *   - `createTRPCReact` is designed to be called ONCE per router type;
 *     the returned object is referentially stable + safe to import
 *     anywhere in the tree.
 *   - Tests that want isolation construct their own
 *     `createTrpcReactClient()` (below) and pass it via `<trpc.Provider>`.
 */
export const trpc: CreateTRPCReact<BffRouter, unknown> = createTRPCReact<BffRouter>();

/**
 * Build the link list. Exported so tests can re-use it with a fake
 * fetch.
 *
 * **No `loggerLink`** at v1. The browser DevTools already log
 * outgoing fetches; doubling the noise hurts more than helps. Add
 * back if a future debugging session demands it.
 */
export function buildTrpcLinks(opts: {
  /** BFF tRPC base URL. Defaults to `/trpc` (same-origin via Vite proxy in dev). */
  readonly trpcUrl?: string;
  /** Fetch override for tests. Defaults to `globalThis.fetch`. */
  readonly fetchFn?: typeof fetch;
}): Array<ReturnType<typeof httpLink>> {
  const url = opts.trpcUrl ?? '/trpc';
  return [
    httpLink({
      url,
      // CRITICAL — cookies on every request. Without this the
      // browser refuses to send `sbp_session` cross-origin (and even
      // some same-origin scenarios per CORS rules).
      fetch: (input, init) => {
        const baseFetch = opts.fetchFn ?? globalThis.fetch;
        // fn-1.31 — wrap the fetch with the active client-otel
        // handle's `wrapFetch` so every tRPC outbound carries a
        // `traceparent` header. The handle is set by
        // `ClientOtelProvider` once `whoAmI` resolves; before that
        // (boot-time probes like `whoAmI` itself), the handle is
        // null and the wrap is a no-op identity. The wrapped fetch
        // consumes `mostRecentSpanId` per-call so a click → mutate
        // chain stays linked but subsequent polling doesn't inherit
        // the click's parent.
        const handle = getActiveClientOtelHandle();
        const wrapped = handle === null ? baseFetch : handle.wrapFetch(baseFetch);
        // fn-45.4 — one-shot retry on the BFF's `503 SESSION_REFRESH_RETRY`.
        // A benign multi-instance refresh race returns this NON-revoking
        // status; by the time we retry, the winning request has installed
        // the rotated `__Host-` cookies the browser re-presents. Without
        // this the SPA surfaces a transient refresh race as a hard failure
        // (React Query has retries disabled).
        const f = retryableRefreshFetch(wrapped);
        // We intentionally rebuild `init` rather than spread it —
        // tRPC's typing for `fetch` passes an `init` whose `signal`
        // is `AbortSignal | undefined`, while `RequestInit.signal`
        // (with `exactOptionalPropertyTypes`) demands
        // `AbortSignal | null | undefined-absent`. Spreading lands
        // an explicit `signal: undefined`, which trips the strict
        // type. Building a new object + only setting `signal` when
        // truthy avoids the round-trip.
        const next: RequestInit =
          init === undefined ? {} : { ...init, signal: init.signal ?? null };
        next.credentials = 'include';
        // fn-45.4 — the BFF CSRF preHandler requires a custom header on
        // every unsafe method (tRPC POSTs both queries + mutations). A
        // cross-site HTML form can't set a custom header; this marks the
        // request as a same-origin programmatic fetch.
        const headers = new Headers(next.headers);
        if (!headers.has('X-Requested-With')) headers.set('X-Requested-With', 'fetch');
        next.headers = headers;
        return f(input, next);
      },
    }),
  ];
}

/**
 * Build a vanilla tRPC client. Useful outside React (router loaders,
 * one-off probes from `main.tsx`). Same link config as the React
 * client; the React client wraps this internally via `trpc.useUtils`.
 */
export function createVanillaTrpcClient(
  opts: {
    readonly trpcUrl?: string;
    readonly fetchFn?: typeof fetch;
  } = {},
): CreateTRPCClient<BffRouter> {
  // Annotate the return type explicitly. Without it, the inferred type
  // pulls names from the BFF's nested router modules (`routers/agents`,
  // `routers/approvals`, `routers/runs`, `routers/input-helpers`) into
  // the emitted `.d.ts`. tsc's "type cannot be named without a
  // reference" error fires because those nested paths aren't reachable
  // through `@sb-ai-platform/bff`'s `exports` map. Pinning the return
  // type to `CreateTRPCClient<BffRouter>` (which IS exported) keeps the
  // surface portable.
  return createTRPCClient<BffRouter>({
    links: buildTrpcLinks(opts),
  });
}

/**
 * Factory for the React client + matching React-Query `QueryClient`.
 * Used by `main.tsx`. Tests construct their own pair for isolation.
 *
 * **QueryClient defaults:**
 *   - `staleTime: 0` — caches are invalidated immediately by default.
 *     Each surface opts in to longer caching where appropriate. This
 *     avoids the "I updated the agent and the list still shows the old
 *     name" gotcha that bites un-tuned react-query setups.
 *   - `retry: false` for queries — failed queries do NOT auto-retry.
 *     A 401 from the BFF means "session expired"; auto-retry just
 *     delays the redirect to login by an extra second or two. Each
 *     route's loader explicitly handles 401 → login redirect.
 *   - `refetchOnWindowFocus: true` — common-sense default; brings the
 *     UI in sync when the user tabs back to the console.
 */
export interface TrpcReactClientBundle {
  readonly queryClient: QueryClient;
  readonly trpcClient: ReturnType<typeof trpc.createClient>;
}

export function createTrpcReactClient(
  opts: {
    readonly trpcUrl?: string;
    readonly fetchFn?: typeof fetch;
  } = {},
): TrpcReactClientBundle {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
  const trpcClient = trpc.createClient({
    links: buildTrpcLinks(opts),
  });
  return { queryClient, trpcClient };
}
